import { useContext, useEffect, useState } from "react";
import { QueryClientContext, useQuery } from "@tanstack/react-query";
import { Roles } from "../../auth/roles";
import { HEADER_QK, headerApi } from "../../api/headerApi";

// A person's avatar (release 1.5.0, ported from Summit Engineering's Avatar): their own photo when My profile has one,
// otherwise initials in a calm brand circle, with a ring tinted by their highest role. The ring is decoration — the role
// is always also written in the menu and the accessible name. Never shown on the wall display.

const RING: Record<string, string> = {
  [Roles.Admin]: "ring-purple-500",
  [Roles.OperationsManager]: "ring-brand-600",
  [Roles.MasterScheduler]: "ring-brand-500",
  [Roles.Planner]: "ring-sky-500",
  [Roles.ShopSupervisor]: "ring-sky-400",
  [Roles.PlantManager]: "ring-brand-600",
  [Roles.Quality]: "ring-emerald-600",
  [Roles.Purchasing]: "ring-amber-500",
  [Roles.Executive]: "ring-slate-500",
  [Roles.OperationsViewer]: "ring-slate-300"
};

const ORDER = [
  Roles.Admin,
  Roles.OperationsManager,
  Roles.PlantManager,
  Roles.MasterScheduler,
  Roles.Planner,
  Roles.ShopSupervisor,
  Roles.Quality,
  Roles.Purchasing,
  Roles.Executive,
  Roles.OperationsViewer
] as const;

/** The role that tints the ring (Admin › Operations Manager › Plant Manager › Master Scheduler › Planner › …). */
export function primaryRole(roles: readonly string[] | undefined): string | null {
  if (!roles) return null;
  return ORDER.find((r) => roles.includes(r)) ?? null;
}

/** First and last initials ("Last, First" understood, parentheses ignored). */
export function initialsOf(name: string | null | undefined): string {
  let text = (name ?? "").replace(/\([^)]*\)/g, " ").trim();
  if (text.includes(",")) {
    const [last, first] = text.split(",", 2).map((s) => s.trim());
    text = `${first ?? ""} ${last ?? ""}`.trim();
  }
  const words = text.split(/\s+/).filter((w) => /^[\p{L}\p{N}]/u.test(w));
  if (words.length === 0) return "?";
  if (words.length === 1) return words[0]![0]!.toUpperCase();
  return `${words[0]![0]!.toUpperCase()}${words[words.length - 1]![0]!.toUpperCase()}`;
}

const SIZE = {
  sm: "h-8 w-8 text-xs ring-2",
  md: "h-10 w-10 text-sm ring-2"
} as const;

export function Avatar({ name, roles, size = "sm", photo = false }: { name: string; roles?: readonly string[]; size?: keyof typeof SIZE; photo?: boolean }) {
  const role = primaryRole(roles);
  const ring = role ? RING[role] : "ring-slate-200";
  const text = initialsOf(name);
  const client = useContext(QueryClientContext);
  return (
    <span
      aria-hidden="true"
      data-testid="avatar"
      data-role={role ?? "none"}
      className={`relative inline-flex flex-none select-none items-center justify-center overflow-hidden rounded-full bg-gradient-to-br from-brand-50 to-brand-100 font-semibold tracking-tight text-brand-700 ring-offset-2 ring-offset-white ${SIZE[size]} ${ring}`}
    >
      {photo && client ? <PhotoOrInitials text={text} /> : text}
    </span>
  );
}

function PhotoOrInitials({ text }: { text: string }) {
  const q = useQuery({ queryKey: [...HEADER_QK.photo], queryFn: headerApi.myPhoto, staleTime: 10 * 60_000, gcTime: 30 * 60_000, retry: false, refetchOnWindowFocus: false });
  const [url, setUrl] = useState<string | null>(null);
  const [broken, setBroken] = useState(false);
  useEffect(() => {
    const blob = q.data;
    if (!(blob instanceof Blob) || typeof URL.createObjectURL !== "function") {
      setUrl(null);
      return;
    }
    const next = URL.createObjectURL(blob);
    setUrl(next);
    setBroken(false);
    return () => URL.revokeObjectURL?.(next);
  }, [q.data]);
  if (!url || broken) return <>{text}</>;
  return <img src={url} alt="" data-testid="avatar-photo" className="h-full w-full rounded-full object-cover" onError={() => setBroken(true)} />;
}
